import {Component, Input, signal, inject} from '@angular/core';
import {Team} from "../../../../../models/team";
import {
  NbButtonModule,
  NbCardModule,
  NbDatepickerModule,
  NbDialogRef,
  NbIconModule,
  NbInputModule,
  NbLayoutModule,
  NbOptionModule,
  NbRadioModule,
  NbSelectModule,
  NbToastrService
} from "@nebular/theme";
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {notificationsParams} from "../../../../../shared/settings";
import {MatchesService} from "../../../../../services/matches.service";
import {debounceTime, distinctUntilChanged, forkJoin, Observable, switchMap} from "rxjs";
import {TeamsService} from "../../../../../services/teams.service";
import {GamesService} from "../../../../../services/games-service.service";
import {FormSelectComponent} from "../../../../../shared/components/forms/form-select/form-select.component";
import {FormRadioComponent} from "../../../../../shared/components/forms/form-radio/form-radio.component";
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {
  FormDatetimeMultipartsComponent
} from "../../../../../shared/components/forms/form-datetime-multiparts/form-datetime-multiparts.component";

interface Item {
  team_id: string;
  team_name: string;
}

@Component({
  selector: 'app-organize-match-form',
  standalone: true,
  imports: [
    TranslateModule,
    NbButtonModule,
    NbCardModule,
    NbIconModule,
    ReactiveFormsModule,
    NbOptionModule,
    NbSelectModule,
    NbDatepickerModule,
    NbRadioModule,
    ReactiveFormsModule,
    NbLayoutModule,
    NbInputModule,
    FormSelectComponent,
    FormRadioComponent,
    FormDatetimeMultipartsComponent
  ],
  templateUrl: './organize-match-form.component.html',
  styleUrl: './organize-match-form.component.scss'
})
export class OrganizeMatchFormComponent {
  private readonly translate = inject(TranslateService);
  organizeMatchForm!: FormGroup;

  @Input() currentUserTeam!: Team;
  @Input() selectedOpponentTeam!: Team;
  @Input() selectedDivisionIdSignal!: any;
  @Input() selectedGroupIdSignal!: any;

  searchControl = new FormControl('');
  filteredOptions$: Observable<void> | undefined;
  minDate = new Date();
  maxDate = new Date(new Date().getFullYear() + 1, 11, 31);
  teamsList = signal<any[]>([]);
  gamesList = signal<any[]>([]);

  constructor(private fb: FormBuilder, private gamesService: GamesService, private teamsService: TeamsService,
              private matchesService: MatchesService, private toastrService: NbToastrService, protected dialogRef: NbDialogRef<any>) {}

  ngOnInit(): void {
    this.organizeMatchForm = this.fb.group({
      game_id: ['', [Validators.required]],
      division_id: [this.selectedDivisionIdSignal(), [Validators.required]],
      group_id: [this.selectedGroupIdSignal(), [Validators.required]],
      team_id_1: ['', [Validators.required]],
      team_id_2: [this.selectedOpponentTeam.team_id, [Validators.required]],
      scheduled_start_time: ['', [Validators.required]],
      cast: [true, Validators.required]
    });

    const observable = forkJoin({
      dropDownListGames: this.gamesService.dropDownList(),
      dropDownListTeams: this.teamsService.dropDownListConnectedUserTeams()
    });

    observable.subscribe((result) => {
      this.teamsList.set(result.dropDownListTeams);
      this.gamesList.set(result.dropDownListGames);

      if (result.dropDownListTeams.length > 0) {
        this.organizeMatchForm.patchValue({team_id_1: result.dropDownListTeams[0].team_id});
      }

      if (result.dropDownListGames.length > 0) {
        this.organizeMatchForm.patchValue({game_id: result.dropDownListGames[0].game_id});
      }

      this.filteredOptions$ = this.searchControl.valueChanges.pipe(
        debounceTime(3000),
        distinctUntilChanged(),
        switchMap(async (searchTerm) => this.searchTeam(searchTerm || ''))
      );
    });
  }

  searchTeam(searchTerm: string) {
    if (!searchTerm || searchTerm.length < 2) {
      this.teamsService.dropdownList(searchTerm).subscribe((result: any) => {
        this.teamsList.set(result);
      });
    }
  }

  onCurrentUserTeamSelectionChange(item: any) {
    this.organizeMatchForm.patchValue({team_id_1: item});
  }

  onGameSelectionChange(item: any) {
    this.organizeMatchForm.patchValue({game_id: item});
  }

  cancel() {
    this.dialogRef.close();
  }

  submit() {
    if (this.organizeMatchForm.valid) {
      const formValue = this.organizeMatchForm.value;

      // Extraire l'heure et les minutes du champ time (format HH:mm)
      const [hours, minutes] = formValue.scheduled_start_time.toTimeString().split(':').map(Number);

      // Créer scheduled_start_time en combinant date et time
      const scheduled_start_time = new Date(formValue.scheduled_start_time);
      scheduled_start_time.setHours(hours, minutes, 0, 0);

      const requestData = {
        ...formValue,
        scheduled_start_time: scheduled_start_time.toISOString(),
      };

      this.matchesService.organizeMatch(requestData).subscribe((response: any) => {
        this.toastrService.success(this.translate.instant('games.match.saved'), this.translate.instant('common.information'), notificationsParams);
        this.dialogRef.close();
      });
    }
  }

  getControl(controlName: string): FormControl {
    return this.organizeMatchForm.get(controlName) as FormControl;
  }
}
